import type { PayloadAction } from '@reduxjs/toolkit';
import { logger } from 'lib/logger';
import { splitEvery } from 'ramda';
import { updateUploadFilesActionBatcher } from 'reactApp/modules/uploading/serviceClasses/batchHelpers';
import { isCancellable, isFolder } from 'reactApp/modules/uploadList/uploadList.getters';
import { removeFields } from 'reactApp/modules/uploadList/uploadList.helpers';
import {
    type IChangeItem,
    type IInputFile,
    type INormalizedInputFile,
    type ISelectedError,
    type IUpdateInputFile,
    EFileError,
    EFileStatus,
} from 'reactApp/modules/uploadList/uploadList.model';
import { updateUploadFilesAction } from 'reactApp/modules/uploadList/uploadList.module';
import { getGroupedFiles } from 'reactApp/modules/uploadList/uploadList.selectors';
import { call, put, select } from 'redux-saga/effects';

export function* removeUploadFiles(action: PayloadAction<IChangeItem[]>) {
    const { payload } = action;

    const res: IInputFile[] = [];

    const groupedFiles: INormalizedInputFile = yield select(getGroupedFiles);

    const processItem = (file: IInputFile, isFolderItem: boolean, id: string) => {
        if (
            (isFolderItem && (file.cloudPath.startsWith(id) || file.folderCloudPath?.startsWith(id))) ||
            file.cloudPath === id ||
            file.descriptorId === id
        ) {
            res.push({ ...file, status: EFileStatus.DELETING });
        }
    };

    for (const element of payload) {
        const { id, isFolder: isFolderItem } = element;

        for (const groupedFile in groupedFiles) {
            const files = groupedFiles[groupedFile].files;

            if (files) {
                for (const key in files) {
                    // удаляем элементы внутри папки, саму папку удалим в updateFileInFolder
                    // или удаляем отдельный файл в папке куда заливали изначально или внутри загруженной папки
                    processItem(files[key], isFolderItem, id);
                }
            }

            processItem(groupedFiles[groupedFile], isFolderItem, id);
        }
    }

    if (res.length) {
        yield put(updateUploadFilesAction(res));
    }
}

export function* cancelAllUploadFile() {
    try {
        const result = yield call(doActionOnFile, cancelFile, true);

        if (result?.length > 0) {
            const chunks = splitEvery<IUpdateInputFile>(500, result);

            for (const chunk of chunks) {
                yield put(updateUploadFilesAction(chunk));
            }
        }
    } catch (error) {
        logger.error(error);
    }
}

export function* hideErrorFiles(action: PayloadAction<ISelectedError>) {
    const { payload } = action;

    if (!payload?.userClick) {
        return;
    }

    const hideErrorFileWrap = (file) => hideErrorFile(file, payload.file);
    yield call(doActionOnFile, hideErrorFileWrap);
}

/* eslint-disable require-yield */
function cancelFile(file: IInputFile) {
    if (!isCancellable(file)) {
        return;
    }
    const editedFile: IInputFile = { ...file };
    editedFile.status = EFileStatus.CANCEL;
    editedFile.error = EFileError.CANCELLED_FILE;

    return editedFile;
}

function* hideErrorFile(file: IInputFile, payload?: IInputFile) {
    if (isFolder(file) || !payload) {
        return;
    }

    const editedFile: IInputFile = removeFields(file);

    if (isFolder(payload)) {
        // если этот файл принадлежит выбранной папке, то показываем ошибку, иначе скрываем
        const hideError = !(payload.files && payload.files[file.descriptorId]);

        updateUploadFilesActionBatcher.push({ ...editedFile, hideError });
    } else if (file.error && file.hideError) {
        // если выбранный элемент не папка, то показываем все ошибки
        yield put(updateUploadFilesAction({ ...editedFile, hideError: false }));
    }
}

function* doActionOnFile(callback: (file: IInputFile) => void, notGenericCallback = false) {
    const groupedFiles: INormalizedInputFile = yield select(getGroupedFiles);

    const result: IInputFile[] = [];

    for (const groupedFile in groupedFiles) {
        const files = groupedFiles[groupedFile].files;

        if (files) {
            for (const key in files) {
                const res = notGenericCallback ? callback(files[key]) : yield callback(files[key]);
                if (res) {
                    result.push(res);
                }
            }
        }

        const res = notGenericCallback ? callback(groupedFiles[groupedFile]) : yield callback(groupedFiles[groupedFile]);
        if (res) {
            result.push(res);
        }
    }

    return result;
}
